import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Trash2, Plus, Package, MapPin, Inbox, Mail, Phone, RefreshCw, Upload, Download, FileSpreadsheet, AlertCircle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const PRODUCT_CATEGORIES = [
  "Flashing and Adhesives",
  "Decking",
  "Siding",
  "Hardware and Fasteners",
  "Insulation",
  "HouseWrap",
  "Exterior Doors",
  "Interior Doors",
  "Structural Hardware",
  "Tools",
];

const MFR_STATUSES = ["new", "contacted", "quoted", "won", "lost"];
const MFR_STATUS_COLORS = {
  new: "bg-[#C62828] text-white",
  contacted: "bg-yellow-500 text-white",
  quoted: "bg-blue-600 text-white",
  won: "bg-green-700 text-white",
  lost: "bg-slate-500 text-white",
};

export default function ManufacturerDashboard() {
  const { user } = useAuth();
  const [tab, setTab] = useState("leads");
  const [products, setProducts] = useState([]);
  const [dealers, setDealers] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, d, l] = await Promise.all([
        api.get("/products"),
        api.get("/dealers"),
        api.get("/my-leads"),
      ]);
      setProducts(p.data); setDealers(d.data); setLeads(l.data);
    } catch (e) {
      console.error("Failed to load dashboard data:", e);
      toast.error("Failed to load data");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const newLeadCount = leads.filter((l) => l.mfr_status === "new").length;

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14" data-testid="manufacturer-dashboard">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-tag">Manufacturer Dashboard</div>
          <h1 className="font-heading text-4xl md:text-6xl font-black uppercase mt-1 leading-none text-[#1A365D]">
            {user?.company_name || "Your company"}
          </h1>
          <p className="mt-2 text-slate-600">Track leads routed to your dealer network, and manage your products and dealers.</p>
        </div>
        <div className="brutal-card-flat px-5 py-3 font-mono-ll text-xs">
          <div className="text-slate-500 tracking-widest">SIGNED IN AS</div>
          <div className="text-[#1A365D]">{user?.email}</div>
        </div>
      </div>

      <div className="mt-10 border-b-2 border-[#1A365D] flex gap-1 flex-wrap">
        <TabBtn active={tab==="leads"} onClick={() => setTab("leads")} icon={Inbox} label={
          <>Leads ({leads.length}){newLeadCount > 0 && <span className="ml-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 bg-[#C62828] text-white text-[10px] font-mono-ll rounded-sm">{newLeadCount}</span>}</>
        } testid="tab-leads"/>
        <TabBtn active={tab==="products"} onClick={() => setTab("products")} icon={Package} label={`Products (${products.length})`} testid="tab-products"/>
        <TabBtn active={tab==="dealers"} onClick={() => setTab("dealers")} icon={MapPin} label={`Dealers (${dealers.length})`} testid="tab-dealers"/>
      </div>

      {loading ? (
        <div className="py-20 text-center font-mono-ll text-sm text-slate-500 tracking-widest">LOADING…</div>
      ) : tab === "leads" ? (
        <LeadsTab leads={leads} reload={load}/>
      ) : tab === "products" ? (
        <ProductsTab products={products} reload={load}/>
      ) : (
        <DealersTab dealers={dealers} reload={load}/>
      )}
    </div>
  );
}

function TabBtn({ active, onClick, icon: Icon, label, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`flex items-center gap-2 px-5 py-3 font-bold uppercase tracking-widest text-sm border-2 -mb-[2px] ${
        active ? "bg-[#1A365D] text-white border-[#1A365D]" : "bg-transparent text-[#1A365D] border-transparent hover:bg-slate-100"
      }`}>
      <Icon size={16}/> {label}
    </button>
  );
}

function ProductsTab({ products, reload }) {
  const [form, setForm] = useState({ name:"", category: PRODUCT_CATEGORIES[0], description:"" });
  const [saving, setSaving] = useState(false);
  const update = (k,v) => setForm((f) => ({...f, [k]: v}));

  const add = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.description.trim()) { toast.error("Name and description required."); return; }
    setSaving(true);
    try {
      await api.post("/products", form);
      toast.success("Product added");
      setForm({ name:"", category: PRODUCT_CATEGORIES[0], description:"" });
      reload();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try { await api.delete(`/products/${id}`); toast.success("Deleted"); reload(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  return (
    <div className="mt-8 grid md:grid-cols-12 gap-6">
      <form onSubmit={add} className="md:col-span-5 brutal-card p-6 space-y-4 self-start">
        <h2 className="font-heading text-xl font-bold uppercase text-[#1A365D]">Add Product</h2>
        <div><label className="label-tag block mb-2">Name *</label>
          <input className="ll-input" value={form.name} onChange={(e) => update("name", e.target.value)} data-testid="product-name-input"/></div>
        <div><label className="label-tag block mb-2">Category</label>
          <select className="ll-select" value={form.category} onChange={(e) => update("category", e.target.value)} data-testid="product-category-select">
            {PRODUCT_CATEGORIES.map((c) => <option key={c}>{c}</option>)}
          </select></div>
        <div><label className="label-tag block mb-2">Description *</label>
          <textarea className="ll-textarea" value={form.description} onChange={(e) => update("description", e.target.value)} data-testid="product-description-input"/></div>
        <button className="btn-primary w-full flex items-center justify-center gap-2" disabled={saving} data-testid="add-product-btn">
          <Plus size={16}/> {saving ? "Saving…" : "Add Product"}
        </button>
      </form>

      <div className="md:col-span-7 space-y-3">
        {products.length === 0 ? (
          <div className="brutal-card-flat p-8 text-center text-slate-500 font-mono-ll text-xs tracking-widest">NO PRODUCTS YET</div>
        ) : products.map((p) => (
          <div key={p.id} className="brutal-card-flat p-5 flex items-start justify-between gap-4" data-testid={`product-row-${p.id}`}>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-heading text-lg font-bold uppercase text-[#1A365D]">{p.name}</span>
                <span className="font-mono-ll text-[10px] tracking-widest text-[#C62828] border border-[#C62828] px-2 py-0.5">{p.category}</span>
              </div>
              <div className="text-sm text-slate-700 mt-1">{p.description}</div>
            </div>
            <button onClick={() => remove(p.id)} className="text-[#C62828] hover:bg-red-50 p-2" data-testid={`delete-product-${p.id}`}>
              <Trash2 size={18}/>
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function DealersTab({ dealers, reload }) {
  const [form, setForm] = useState({ name:"", city:"", state:"", zip_code:"", phone:"", email:"", contact_name:"" });
  const [saving, setSaving] = useState(false);
  const update = (k,v) => setForm((f) => ({...f, [k]: v}));

  const add = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/dealers", form);
      toast.success("Dealer added");
      setForm({ name:"", city:"", state:"", zip_code:"", phone:"", email:"", contact_name:"" });
      reload();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this dealer?")) return;
    try { await api.delete(`/dealers/${id}`); toast.success("Deleted"); reload(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  return (
    <div className="mt-8 grid md:grid-cols-12 gap-6">
      <form onSubmit={add} className="md:col-span-5 brutal-card p-6 space-y-4 self-start">
        <h2 className="font-heading text-xl font-bold uppercase text-[#1A365D]">Add Dealer</h2>
        <div><label className="label-tag block mb-2">Dealer Name *</label>
          <input required className="ll-input" value={form.name} onChange={(e) => update("name", e.target.value)} data-testid="dealer-name-input"/></div>
        <div><label className="label-tag block mb-2">Contact Person</label>
          <input className="ll-input" value={form.contact_name} onChange={(e) => update("contact_name", e.target.value)} data-testid="dealer-contact-input"/></div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="label-tag block mb-2">City *</label><input required className="ll-input" value={form.city} onChange={(e) => update("city", e.target.value)} data-testid="dealer-city-input"/></div>
          <div><label className="label-tag block mb-2">State *</label><input required maxLength={2} className="ll-input uppercase" value={form.state} onChange={(e) => update("state", e.target.value.toUpperCase())} data-testid="dealer-state-input"/></div>
        </div>
        <div><label className="label-tag block mb-2">ZIP *</label><input required maxLength={5} className="ll-input" value={form.zip_code} onChange={(e) => update("zip_code", e.target.value.replace(/\D/g,""))} data-testid="dealer-zip-input"/></div>
        <div><label className="label-tag block mb-2">Phone *</label><input required className="ll-input" value={form.phone} onChange={(e) => update("phone", e.target.value)} data-testid="dealer-phone-input"/></div>
        <div><label className="label-tag block mb-2">Email *</label><input required type="email" className="ll-input" value={form.email} onChange={(e) => update("email", e.target.value)} data-testid="dealer-email-input"/></div>
        <button className="btn-primary w-full flex items-center justify-center gap-2" disabled={saving} data-testid="add-dealer-btn">
          <Plus size={16}/> {saving ? "Saving…" : "Add Dealer"}
        </button>
      </form>

      <div className="md:col-span-7 space-y-4">
        <BulkUploadCard reload={reload} dealersCount={dealers.length}/>
        {dealers.length === 0 ? (
          <div className="brutal-card-flat p-8 text-center text-slate-500 font-mono-ll text-xs tracking-widest">NO DEALERS YET</div>
        ) : dealers.map((d) => (
          <div key={d.id} className="brutal-card-flat p-5 flex items-start justify-between gap-4" data-testid={`dealer-row-${d.id}`}>
            <div>
              <div className="font-heading text-lg font-bold uppercase text-[#1A365D]">{d.name}</div>
              <div className="text-sm text-slate-700">{d.city}, {d.state} {d.zip_code}</div>
              <div className="mt-1 font-mono-ll text-xs text-slate-500">{d.phone} · {d.email}{d.contact_name && ` · ${d.contact_name}`}</div>
            </div>
            <button onClick={() => remove(d.id)} className="text-[#C62828] hover:bg-red-50 p-2" data-testid={`delete-dealer-${d.id}`}>
              <Trash2 size={18}/>
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}


function LeadsTab({ leads, reload }) {
  const [filter, setFilter] = useState("all");

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/my-leads/${id}/status`, { status });
      toast.success(`Status → ${status.toUpperCase()}`);
      reload();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const filtered = filter === "all" ? leads : leads.filter((l) => l.mfr_status === filter);
  const stats = MFR_STATUSES.reduce((acc, s) => { acc[s] = leads.filter((l) => l.mfr_status === s).length; return acc; }, {});

  if (leads.length === 0) {
    return (
      <div className="mt-12 brutal-card-flat p-12 text-center">
        <Inbox className="mx-auto text-slate-400" size={48} strokeWidth={1.5}/>
        <h2 className="mt-4 font-heading text-2xl font-bold uppercase text-[#1A365D]">No leads routed yet</h2>
        <p className="mt-2 text-slate-600 max-w-lg mx-auto">
          When a contractor submits a request matching your dealer network&apos;s ZIPs or state, it&apos;ll appear here automatically. Make sure you have dealers added for the markets you cover.
        </p>
      </div>
    );
  }

  return (
    <div className="mt-8">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <StatBox label="Total" value={leads.length} accent/>
        {MFR_STATUSES.map((s) => (
          <StatBox key={s} label={s} value={stats[s] || 0}/>
        ))}
      </div>

      {/* Filter */}
      <div className="mt-6 flex items-center gap-3 flex-wrap">
        <span className="label-tag">Filter</span>
        <select value={filter} onChange={(e) => setFilter(e.target.value)} className="ll-select max-w-[220px]" data-testid="my-leads-filter">
          <option value="all">All Statuses</option>
          {MFR_STATUSES.map((s) => <option key={s} value={s}>{s.toUpperCase()}</option>)}
        </select>
        <button onClick={reload} className="btn-ghost flex items-center gap-2" data-testid="my-leads-refresh"><RefreshCw size={14}/> Refresh</button>
      </div>

      {/* Lead cards */}
      <div className="mt-6 space-y-4">
        {filtered.length === 0 ? (
          <div className="brutal-card-flat p-10 text-center font-mono-ll text-xs tracking-widest text-slate-500">NO LEADS MATCH THIS FILTER</div>
        ) : filtered.map((l) => (
          <LeadCard key={l.id} lead={l} onStatus={(s) => updateStatus(l.id, s)}/>
        ))}
      </div>
    </div>
  );
}

function StatBox({ label, value, accent }) {
  return (
    <div className={`p-4 border-2 ${accent ? "border-[#C62828] bg-[#C62828] text-white" : "border-[#1A365D] bg-white"}`}>
      <div className={`font-mono-ll text-[10px] tracking-widest uppercase ${accent ? "text-white/80" : "text-slate-500"}`}>{label}</div>
      <div className={`font-heading text-3xl font-black mt-1 ${accent ? "text-white" : "text-[#1A365D]"}`}>{value}</div>
    </div>
  );
}

function LeadCard({ lead, onStatus }) {
  const [expanded, setExpanded] = useState(false);
  const location = [lead.city, lead.state].filter(Boolean).join(", ") || `ZIP ${lead.zip_code}`;
  return (
    <div className="brutal-card-flat p-5" data-testid={`my-lead-${lead.id}`}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-[220px]">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`px-2 py-1 text-[10px] font-mono-ll tracking-widest uppercase ${MFR_STATUS_COLORS[lead.mfr_status]}`}>{lead.mfr_status}</span>
            <span className="font-mono-ll text-[10px] tracking-widest text-slate-500">{location} · ZIP {lead.zip_code}</span>
          </div>
          <h3 className="mt-2 font-heading text-2xl font-bold uppercase text-[#1A365D] leading-tight">{lead.product_type}</h3>
          <div className="mt-1 text-sm text-slate-600">{lead.quantity}</div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <select value={lead.mfr_status} onChange={(e) => onStatus(e.target.value)} className="ll-select text-xs py-1" data-testid={`my-lead-status-${lead.id}`}>
            {MFR_STATUSES.map((s) => <option key={s} value={s}>{s.toUpperCase()}</option>)}
          </select>
          <button onClick={() => setExpanded(!expanded)} className="btn-ghost text-xs" data-testid={`my-lead-expand-${lead.id}`}>
            {expanded ? "Collapse ▲" : "Details ▼"}
          </button>
        </div>
      </div>

      {/* Always-visible contact summary */}
      <div className="mt-3 flex flex-wrap gap-4 text-sm">
        <a href={`mailto:${lead.email}`} className="flex items-center gap-2 text-[#C62828] hover:underline">
          <Mail size={14}/> {lead.email}
        </a>
        <a href={`tel:${lead.phone}`} className="flex items-center gap-2 text-[#C62828] hover:underline">
          <Phone size={14}/> {lead.phone}
        </a>
        <span className="text-slate-500 text-xs font-mono-ll tracking-widest uppercase">
          Submitted {new Date(lead.created_at).toLocaleString()}
        </span>
      </div>

      {expanded && (
        <div className="mt-4 pt-4 border-t-2 border-slate-200 space-y-3">
          <DetailRow k="Contractor" v={`${lead.contractor_name}${lead.company ? " · " + lead.company : ""}`}/>
          <DetailRow k="Specifications" v={lead.specifications} pre/>
          <DetailRow k="Timeline" v={lead.timeline || "—"}/>
          <DetailRow k="Budget" v={lead.budget || "—"}/>
          {lead.notes && <DetailRow k="Notes" v={lead.notes} pre/>}

          <div>
            <div className="label-tag mb-2">Your matching dealers ({lead.matched_dealers?.length || 0})</div>
            {(!lead.matched_dealers || lead.matched_dealers.length === 0) ? (
              <div className="text-sm text-slate-500">No dealer info attached.</div>
            ) : (
              <div className="space-y-2">
                {lead.matched_dealers.map((d) => (
                  <div key={d.id} className="border-2 border-[#1A365D] p-3 text-sm">
                    <div className="font-bold text-[#1A365D]">{d.name}</div>
                    <div className="text-slate-700">{d.city}, {d.state} {d.zip_code}</div>
                    <div className="text-xs text-slate-500 font-mono-ll mt-1">{d.phone} · {d.email}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function DetailRow({ k, v, pre }) {
  return (
    <div className="border-l-4 border-[#C62828] pl-3">
      <div className="label-tag">{k}</div>
      <div className={`text-[#1A365D] mt-1 text-sm ${pre ? "whitespace-pre-wrap" : ""}`}>{v}</div>
    </div>
  );
}


function BulkUploadCard({ reload, dealersCount }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const fileInputRef = React.useRef(null);

  const onPick = (e) => {
    const f = e.target.files?.[0] || null;
    setFile(f);
    setResult(null);
  };

  const upload = async () => {
    if (!file) { toast.error("Pick a CSV or XLSX file first."); return; }
    setUploading(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/dealers/bulk-upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      if (data.inserted > 0) {
        toast.success(`${data.inserted} dealer${data.inserted === 1 ? "" : "s"} imported`);
        reload();
      } else {
        toast.error("No dealers imported — see errors below.");
      }
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/dealers/template.csv", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = "lumberloop_dealer_template.csv";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Couldn't download template");
    }
  };

  return (
    <div className="brutal-card p-5" data-testid="bulk-upload-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[#C62828] text-white flex items-center justify-center">
            <FileSpreadsheet size={20} strokeWidth={2}/>
          </div>
          <div>
            <h3 className="font-heading text-xl font-bold uppercase text-[#1A365D]">Bulk Upload Dealers</h3>
            <div className="text-xs text-slate-600">Import your whole dealer network from CSV or Excel (.xlsx) in seconds.</div>
          </div>
        </div>
        <button onClick={downloadTemplate} className="btn-ghost text-xs flex items-center gap-2" data-testid="download-template-btn">
          <Download size={14}/> Template CSV
        </button>
      </div>

      <div className="mt-4 flex items-center gap-3 flex-wrap">
        <input ref={fileInputRef} type="file" accept=".csv,.xlsx,.xlsm" onChange={onPick} className="hidden" data-testid="bulk-upload-input"/>
        <button onClick={() => fileInputRef.current?.click()} className="btn-secondary flex items-center gap-2" data-testid="bulk-upload-pick-btn">
          <Upload size={14}/> {file ? "Change file" : "Pick file"}
        </button>
        {file && (
          <span className="font-mono-ll text-xs text-slate-700 truncate max-w-[260px]" title={file.name}>{file.name}</span>
        )}
        <button onClick={upload} disabled={!file || uploading} className="btn-primary text-sm" data-testid="bulk-upload-submit-btn">
          {uploading ? "Uploading…" : "Import"}
        </button>
      </div>

      <details className="mt-4 text-xs text-slate-600">
        <summary className="cursor-pointer font-mono-ll tracking-widest uppercase text-[10px]">Column reference</summary>
        <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2">
          {[
            ["name", "required"],
            ["contact_name", "optional"],
            ["city", "required"],
            ["state", "required (2-letter)"],
            ["zip_code", "required"],
            ["phone", "required"],
            ["email", "required"],
          ].map(([col, req]) => (
            <div key={col} className="border border-slate-300 px-2 py-1">
              <code className="text-[#1A365D] font-mono-ll">{col}</code>
              <div className="text-[10px] text-slate-500">{req}</div>
            </div>
          ))}
        </div>
      </details>

      {result && (
        <div className="mt-4 border-2 border-[#1A365D] p-3 text-sm" data-testid="bulk-upload-result">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="px-2 py-1 bg-green-700 text-white text-[10px] font-mono-ll tracking-widest uppercase">
              {result.inserted} imported
            </span>
            {result.skipped > 0 && (
              <span className="px-2 py-1 bg-yellow-500 text-white text-[10px] font-mono-ll tracking-widest uppercase">
                {result.skipped} skipped
              </span>
            )}
            <span className="text-xs text-slate-500 font-mono-ll">of {result.total_rows} rows</span>
          </div>
          {result.errors?.length > 0 && (
            <div className="mt-2">
              <div className="font-mono-ll text-[10px] tracking-widest text-slate-500 uppercase mb-1">Skipped rows:</div>
              <ul className="space-y-1">
                {result.errors.map((err) => (
                  <li key={`row-${err.row}`} className="flex items-start gap-2 text-xs">
                    <AlertCircle size={12} className="text-yellow-600 mt-0.5 flex-shrink-0"/>
                    <span><strong>Row {err.row}:</strong> {err.error}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
